import React, { useEffect, useState } from "react";
import { Monitor, Trash2, Wifi, WifiOff, ExternalLink, Calendar, Plus, RefreshCw, Clock4, Moon, Zap, Cpu, X, Wrench, Globe2 } from "lucide-react";
import DashboardLayout from "@/components/app/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { RemoteControlDialog } from "@/components/app/RemoteControlDialog";

const headingFont = { fontFamily: "Outfit, sans-serif" };
const accentFont  = { fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontWeight: 500 };

const DAYS = [
  { v: 0, l: "Mon" }, { v: 1, l: "Tue" }, { v: 2, l: "Wed" },
  { v: 3, l: "Thu" }, { v: 4, l: "Fri" }, { v: 5, l: "Sat" }, { v: 6, l: "Sun" },
];

// Mon=mon..Sun=sun — used by the Power Schedule weekly grid.
const PWR_DAYS = [
  { k: "mon", l: "Mon" }, { k: "tue", l: "Tue" }, { k: "wed", l: "Wed" },
  { k: "thu", l: "Thu" }, { k: "fri", l: "Fri" }, { k: "sat", l: "Sat" }, { k: "sun", l: "Sun" },
];

// Most common business/personal timezones. Free-text fallback below the
// Select for power users who want exotic IANA names.
const COMMON_TIMEZONES = [
  "UTC",
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Phoenix", "America/Anchorage", "America/Mexico_City", "America/Sao_Paulo",
  "America/Toronto", "Europe/London", "Europe/Paris", "Europe/Berlin", "Europe/Madrid",
  "Europe/Rome", "Europe/Amsterdam", "Africa/Cairo", "Africa/Lagos", "Asia/Dubai",
  "Asia/Kolkata", "Asia/Singapore", "Asia/Hong_Kong", "Asia/Tokyo", "Asia/Seoul",
  "Asia/Shanghai", "Australia/Sydney", "Pacific/Auckland",
];

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function Screens() {
  const { user } = useAuth();
  const [screens, setScreens] = useState([]);
  const [designs, setDesigns] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [groups, setGroups] = useState([]);
  const [locations, setLocations] = useState([]);
  const [code, setCode] = useState("");
  const [pairing, setPairing] = useState(false);
  const [renamingScreen, setRenamingScreen] = useState(null);
  const [newName, setNewName] = useState("");
  const [scheduleScreen, setScheduleScreen] = useState(null);
  const [scheduleDraft, setScheduleDraft] = useState([]);
  const [powerScreen, setPowerScreen] = useState(null);
  const [powerDraft, setPowerDraft] = useState(null);
  const [flashScreen, setFlashScreen] = useState(null);
  const [flashDraft, setFlashDraft] = useState({ kind: "design", ref_id: "", minutes: 15 });
  const [pairError, setPairError] = useState(null);
  // Ticking 'now' for the live "X min left" countdown on Flash pills.
  const [nowTick, setNowTick] = useState(Date.now());

  const load = async () => {
    const [s, d, g, p, l] = await Promise.all([
      api.get("/screens"),
      api.get("/designs"),
      api.get("/groups"),
      api.get("/playlists"),
      api.get("/locations"),
    ]);
    setScreens(s.data.screens || []);
    setDesigns(d.data.designs || []);
    setGroups(g.data.groups || []);
    setPlaylists(p.data.playlists || []);
    setLocations(l.data.locations || []);
  };

  useEffect(() => {
    load();
    const itv = setInterval(load, 8000);
    return () => clearInterval(itv);
  }, []);

  // Auto-fill the pairing code if the user arrives via the QR deep-link
  // (e.g. /dashboard/screens?code=KPKR2R from scanning the TV's QR)
  useEffect(() => {
    const url = new URL(window.location.href);
    const c = url.searchParams.get("code");
    if (c) {
      setCode(c.toUpperCase());
      // Clean the URL so a refresh doesn't keep trying to re-fill
      url.searchParams.delete("code");
      window.history.replaceState({}, "", url.pathname + url.search + url.hash);
    }
  }, []);

  const handlePair = async (e) => {
    e.preventDefault();
    setPairing(true);
    setPairError(null);
    try {
      const cleaned = code.replace(/[^A-Z0-9]/gi, "").toUpperCase();
      await api.post("/screens/pair", { code: cleaned, name: "New Screen" });
      toast.success("Screen paired");
      setCode("");
      load();
    } catch (err) {
      const msg = formatApiError(err);
      const status = err?.response?.status;
      let kind = "other";
      if (status === 402) kind = "limit";
      else if (status === 404) kind = "invalid";
      else if (status === 400 && /already/i.test(msg)) kind = "already";
      setPairError({ kind, msg });
      toast.error(msg);
    } finally {
      setPairing(false);
    }
  };

  const updateScreen = async (screenId, body, successMsg) => {
    try {
      await api.put(`/screens/${screenId}`, body);
      if (successMsg) toast.success(successMsg);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const renameScreen = async () => {
    try {
      await api.put(`/screens/${renamingScreen.id}`, { name: newName });
      toast.success("Renamed");
      setRenamingScreen(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const removeScreen = async (id) => {
    if (!window.confirm("Unpair this screen?")) return;
    try {
      await api.delete(`/screens/${id}`);
      toast.success("Screen removed");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const refreshScreen = async (id) => {
    try {
      await api.post(`/screens/${id}/refresh`);
      toast.success("Refresh signal sent — TV will reload within 5 s");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openSchedule = (s) => {
    setScheduleScreen(s);
    setScheduleDraft([...(s.schedules || [])]);
  };
  const addRule = () => setScheduleDraft((d) => [...d, { design_id: "", days: [0, 1, 2, 3, 4], start: "08:00", end: "11:00" }]);
  const updateRule = (idx, patch) => setScheduleDraft((d) => d.map((r, i) => i === idx ? { ...r, ...patch } : r));
  const removeRule = (idx) => setScheduleDraft((d) => d.filter((_, i) => i !== idx));
  const toggleDay = (idx, dv) => updateRule(idx, {
    days: scheduleDraft[idx].days.includes(dv)
      ? scheduleDraft[idx].days.filter((x) => x !== dv)
      : [...scheduleDraft[idx].days, dv].sort(),
  });
  const saveSchedule = async () => {
    try {
      await api.put(`/screens/${scheduleScreen.id}`, { schedules: scheduleDraft });
      toast.success("Schedule saved");
      setScheduleScreen(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // ── Power Schedule (operating hours) ─────────────────────────────────────
  // Detect the browser's local timezone so the customer doesn't have to type
  // it on a fresh schedule. They can change it via the dropdown below.
  const _localTZ = (() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; }
    catch { return "UTC"; }
  })();

  const openPowerSchedule = (s) => {
    const existing = s.power_schedule;
    const blank = {
      enabled: true,
      timezone: _localTZ,
      weekly: {
        mon: { start: "09:00", end: "22:00" },
        tue: { start: "09:00", end: "22:00" },
        wed: { start: "09:00", end: "22:00" },
        thu: { start: "09:00", end: "22:00" },
        fri: { start: "09:00", end: "22:00" },
        sat: null,
        sun: null,
      },
      outside_hours_mode: "closed_frame",
    };
    setPowerScreen(s);
    setPowerDraft(existing && existing.weekly ? JSON.parse(JSON.stringify(existing)) : blank);
  };

  const toggleDayOpen = (dayKey) => {
    setPowerDraft((d) => ({
      ...d,
      weekly: {
        ...d.weekly,
        [dayKey]: d.weekly[dayKey] ? null : { start: "09:00", end: "22:00" },
      },
    }));
  };
  const setDayTime = (dayKey, field, value) => {
    setPowerDraft((d) => ({
      ...d,
      weekly: {
        ...d.weekly,
        [dayKey]: { ...(d.weekly[dayKey] || { start: "09:00", end: "22:00" }), [field]: value },
      },
    }));
  };
  const applyToAllDays = () => {
    // Copy Monday's hours to every day that's currently OPEN. Days that are
    // closed stay closed — admin can re-enable each individually.
    const mon = powerDraft?.weekly?.mon;
    if (!mon) {
      toast.error("Set Monday's hours first, then apply to all open days.");
      return;
    }
    setPowerDraft((d) => ({
      ...d,
      weekly: Object.fromEntries(
        Object.entries(d.weekly).map(([k, v]) => [k, v ? { ...mon } : null]),
      ),
    }));
    toast.success("Hours copied to all open days");
  };
  const savePowerSchedule = async () => {
    try {
      await api.put(`/screens/${powerScreen.id}`, { power_schedule: powerDraft });
      toast.success("Power schedule saved");
      setPowerScreen(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // ── Flash Takeover ───────────────────────────────────────────────────────
  // Push a design or playlist to a screen for N minutes, then auto-revert.
  // Killer for emergency alerts, time-bound promos, lunch specials.
  const openFlash = (s) => {
    setFlashScreen(s);
    setFlashDraft({ kind: "design", ref_id: "", minutes: 15 });
  };
  const submitFlash = async () => {
    if (!flashDraft.ref_id) {
      toast.error(`Pick a ${flashDraft.kind} first`);
      return;
    }
    const body = { minutes: Math.max(1, Math.min(1440, parseInt(flashDraft.minutes, 10) || 15)) };
    if (flashDraft.kind === "design") body.design_id = flashDraft.ref_id;
    else body.playlist_id = flashDraft.ref_id;
    try {
      await api.post(`/screens/${flashScreen.id}/flash`, body);
      toast.success(`Flashing for ${body.minutes} min · auto-reverts after`);
      setFlashScreen(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const clearFlash = async (sid) => {
    try {
      await api.delete(`/screens/${sid}/flash`);
      toast.success("Flash cleared · reverting to scheduled content");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  // Live "X min left" countdown — re-renders the pill every 15s without
  // hammering the API.
  useEffect(() => {
    const itv = setInterval(() => setNowTick(Date.now()), 15000);
    return () => clearInterval(itv);
  }, []);
  const _formatRemaining = (endsAt) => {
    if (!endsAt) return "";
    const ms = new Date(endsAt).getTime() - nowTick;
    if (ms <= 0) return "ending…";
    const mins = Math.ceil(ms / 60000);
    if (mins < 60) return `${mins}m left`;
    const h = Math.floor(mins / 60);
    return `${h}h ${mins % 60}m left`;
  };

  const effPlan = user?.effective_plan || user?.plan || "free";
  const isFree = effPlan === "free";
  const atLimit = isFree && screens.length >= 1;

  // ── Remote Command Bridge — uses the shared RemoteControlDialog so both
  // the owner-facing page (here) and the admin Fleet page share one polished
  // UI + one execution path. Only the endpoint layer differs (adminMode).
  const [remoteScreen, setRemoteScreen] = useState(null);
  const openRemote = (s) => setRemoteScreen(s);

  return (
    <DashboardLayout>
      <div className="p-6 md:p-10 max-w-7xl text-slate-900">
        <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-sky-600 mb-3">Screens</div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-semibold" style={headingFont}>
              Paired{" "}
              <span style={accentFont} className="text-sky-600">displays</span>.
            </h1>
          </div>
          {atLimit && (
            <Link to="/dashboard/billing">
              <Button className="rounded-full bg-sky-500 hover:bg-sky-600 text-white shadow-sm" data-testid="upgrade-banner-button">Upgrade to add more screens</Button>
            </Link>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mb-10">
          {/* Pair box */}
          <div
            className="lg:col-span-1 rounded-3xl p-8 relative overflow-hidden border backdrop-blur-xl"
            style={{ background: "linear-gradient(135deg, rgba(139,92,246,0.10), rgba(236,72,153,0.06))", borderColor: "rgba(139,92,246,0.25)", boxShadow: "0 0 30px rgba(139,92,246,0.15)" }}
          >
            <div className="text-[10px] uppercase tracking-[0.3em] text-sky-600 mb-3 font-medium">Pair a new screen</div>
            <h2 className="text-2xl font-semibold tracking-tight mb-3" style={headingFont}>Enter the 6-digit code from your TV</h2>
            <p className="text-sm text-slate-600 mb-6">
              Open <span className="text-sky-600 font-semibold">/tv</span> on the screen first to display the pairing code.
            </p>
            <form onSubmit={handlePair} className="space-y-3" data-testid="pair-form">
              <Input
                value={code}
                onChange={(e) => { setCode(e.target.value.toUpperCase()); if (pairError) setPairError(null); }}
                placeholder="K7DR4Z"
                maxLength={10}
                className="rounded-xl h-12 bg-slate-50 border-slate-200 text-slate-900 text-2xl tracking-widest text-center font-mono"
                data-testid="pair-code-input"
              />
              <Button type="submit" disabled={pairing || code.replace(/[^A-Z0-9]/gi, "").length < 6} className="w-full rounded-full h-11 bg-sky-500 hover:bg-sky-600 text-white shadow-sm font-semibold" data-testid="pair-submit-button">
                {pairing ? "Pairing…" : "Pair screen"}
              </Button>
            </form>
            {pairError && (
              <div className={`mt-4 p-3 rounded-xl border-l-4 ${pairError.kind === "limit" ? "border-purple-400 bg-sky-50" : "border-red-500 bg-red-500/10"}`} data-testid="pair-error-banner" role="alert">
                <div className="text-xs font-semibold uppercase tracking-[0.2em] mb-1" style={{ color: pairError.kind === "limit" ? "rgb(216, 180, 254)" : "rgb(252, 165, 165)" }}>
                  {pairError.kind === "limit"   && "Plan limit reached"}
                  {pairError.kind === "invalid" && "Code not found"}
                  {pairError.kind === "already" && "Already paired"}
                  {pairError.kind === "other"   && "Pairing failed"}
                </div>
                <p className="text-sm text-slate-800 leading-snug">{pairError.msg}</p>
                {pairError.kind === "invalid" && (
                  <p className="text-xs text-slate-500 mt-2">
                    Make sure your TV is showing a code right now (open <span className="font-mono">/tv</span> on it), and that you typed all 6 characters exactly.
                  </p>
                )}
                {pairError.kind === "limit" && (
                  <Link to="/dashboard/billing" className="inline-block mt-2 text-xs font-semibold text-sky-600 hover:underline" data-testid="pair-upgrade-link">
                    Upgrade to Pro →
                  </Link>
                )}
              </div>
            )}
            <div className="mt-6 pt-6 border-t border-slate-200">
              <a href="/tv" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-slate-700 hover:text-slate-900" data-testid="open-tv-link">
                Open TV display in new window <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>

          {/* Screens list */}
          <div className="lg:col-span-2">
            {screens.length === 0 ? (
              <div className="rounded-3xl p-12 border border-dashed text-center" style={{ background: "#F8FAFC", borderColor: "#E2E8F0" }} data-testid="empty-screens">
                <Monitor className="h-10 w-10 mx-auto text-slate-400 mb-4" />
                <h3 className="text-xl font-semibold mb-2" style={headingFont}>No screens paired yet</h3>
                <p className="text-sm text-slate-500 max-w-sm mx-auto">
                  Open <span className="font-mono text-sky-600">/tv</span> on any browser, smart TV or Android stick. A pairing code will appear. Enter it on the left.
                </p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="screens-list">
                {screens.map((s) => {
                  const online = s.last_seen_at && (Date.now() - new Date(s.last_seen_at).getTime()) < 15000;
                  const hasSchedule = (s.schedules || []).length > 0;
                  const ps = s.power_state;
                  const hasPowerSched = s.power_schedule?.enabled;
                  const ov = s.override_state;
                  const di = s.device_info;
                  return (
                    <div key={s.id} className="rounded-2xl p-5 border backdrop-blur-xl" style={{ background: "#FFFFFF", borderColor: "#E2E8F0" }} data-testid={`screen-row-${s.id}`}>
                      <div className="flex flex-wrap items-start gap-4">
                        <div className="flex-1 min-w-[200px]">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h3 className="font-semibold text-lg truncate" style={headingFont}>{s.name || "Untitled screen"}</h3>
                            {online ? (
                              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-medium text-emerald-300"><Wifi className="h-3 w-3" /> Online</span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-medium text-slate-400"><WifiOff className="h-3 w-3" /> Idle</span>
                            )}
                            {hasSchedule && (
                              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-medium text-sky-600"><Calendar className="h-3 w-3" /> Scheduled</span>
                            )}
                            {hasPowerSched && ps && (
                              <span
                                className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-medium ${
                                  ps.is_operating ? "text-emerald-600" : "text-amber-600"
                                }`}
                                data-testid={`screen-power-pill-${s.id}`}
                                title={ps.schedule_label}
                              >
                                {ps.is_operating ? <Clock4 className="h-3 w-3" /> : <Moon className="h-3 w-3" />}
                                {ps.schedule_label}
                              </span>
                            )}
                            {ov?.active && (
                              <span
                                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-amber-700 bg-amber-100 border border-amber-300 rounded-full px-2 h-5"
                                data-testid={`screen-flash-pill-${s.id}`}
                                title={`Flashing: ${ov.name}`}
                              >
                                <Zap className="h-3 w-3 fill-amber-700" />
                                Flashing · {_formatRemaining(ov.ends_at)}
                                <button
                                  onClick={(e) => { e.stopPropagation(); clearFlash(s.id); }}
                                  className="ml-0.5 hover:bg-amber-200 rounded-full p-0.5"
                                  title="Cancel flash now"
                                  data-testid={`screen-flash-stop-${s.id}`}
                                >
                                  <X className="h-2.5 w-2.5" />
                                </button>
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-slate-500">
                            Last seen {timeAgo(s.last_seen_at)} · Code <span className="font-mono text-slate-600">{s.pairing_code}</span>
                          </div>
                          {di && (di.manufacturer || di.model || di.os) && (
                            <div
                              className="inline-flex items-center gap-1 mt-1.5 text-[10px] text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5"
                              data-testid={`screen-device-info-${s.id}`}
                              title={`User-Agent: ${di.user_agent || "—"}`}
                            >
                              <Cpu className="h-2.5 w-2.5" />
                              {[di.manufacturer, di.model].filter(Boolean).join(" ") || di.platform || "Device"}
                              {di.os && <span className="text-slate-400">· {di.os}{di.os_version ? ` ${di.os_version}` : ""}</span>}
                              {di.app_version && <span className="text-slate-400">· v{di.app_version}</span>}
                              {di.screen_resolution && <span className="text-slate-400">· {di.screen_resolution}</span>}
                            </div>
                          )}
                          {/* Network info — LAN IP (from launcher v1.4.3+) + WAN IP (from proxy headers) */}
                          {(s.public_ip || di?.local_ip || di?.wifi_ssid) && (
                            <div
                              className="inline-flex items-center gap-1 mt-1.5 ml-1.5 text-[10px] text-slate-500 bg-sky-50 border border-sky-200 rounded-full px-2 py-0.5"
                              data-testid={`screen-network-info-${s.id}`}
                              title="Network info · used for support triage / ADB access"
                            >
                              <Globe2 className="h-2.5 w-2.5 text-sky-600" />
                              {di?.local_ip && <span className="text-sky-700 font-mono">{di.local_ip}</span>}
                              {di?.wifi_ssid && <span className="text-slate-400">· {di.wifi_ssid}</span>}
                              {s.public_ip && <span className="text-slate-400">· WAN {s.public_ip}</span>}
                              {!di?.local_ip && !s.public_ip && <span className="text-slate-400">no ip</span>}
                            </div>
                          )}
                        </div>
                        <div className="w-full md:w-56">
                          <Label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-medium">Now displaying</Label>
                          <Select value={s.active_design_id || ""} onValueChange={(v) => updateScreen(s.id, { active_design_id: v === "_none" ? "" : v }, "Display updated")}>
                            <SelectTrigger className="rounded-xl h-10 mt-1 bg-slate-50 border-slate-200 text-slate-900" data-testid={`screen-design-select-${s.id}`}>
                              <SelectValue placeholder="Pick a design…" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="_none">— Pairing screen —</SelectItem>
                              {designs.map((d) => (<SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="w-full md:w-48">
                          <Label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-medium">Playlist</Label>
                          <Select value={s.active_playlist_id || ""} onValueChange={(v) => updateScreen(s.id, { active_playlist_id: v === "_none" ? "" : v }, "Playlist updated")}>
                            <SelectTrigger className="rounded-xl h-10 mt-1 bg-slate-50 border-slate-200 text-slate-900" data-testid={`screen-playlist-select-${s.id}`}>
                              <SelectValue placeholder="No playlist" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="_none">— None —</SelectItem>
                              {playlists.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="w-full md:w-48">
                          <Label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-medium">Group</Label>
                          <Select value={s.group_id || ""} onValueChange={(v) => updateScreen(s.id, { group_id: v === "_none" ? "" : v }, "Group updated")}>
                            <SelectTrigger className="rounded-xl h-10 mt-1 bg-slate-50 border-slate-200 text-slate-900" data-testid={`screen-group-select-${s.id}`}>
                              <SelectValue placeholder="No group" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="_none">— None —</SelectItem>
                              {groups.map((g) => (<SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="w-full md:w-48">
                          <Label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-medium">Location</Label>
                          <Select value={s.location_id || ""} onValueChange={(v) => updateScreen(s.id, { location_id: v === "_none" ? "" : v }, "Location updated")}>
                            <SelectTrigger className="rounded-xl h-10 mt-1 bg-slate-50 border-slate-200 text-slate-900" data-testid={`screen-location-select-${s.id}`}>
                              <SelectValue placeholder="No location" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="_none">— None —</SelectItem>
                              {locations.map((l) => (<SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex gap-1.5 self-end flex-wrap">
                          <Button variant="outline" size="sm" className="rounded-full bg-sky-50 border-sky-200 text-sky-700 hover:bg-sky-100" onClick={() => openRemote(s)} title="Remote control: reload, clear cache, unpair, send message" data-testid={`screen-remote-${s.id}`}><Wrench className="h-4 w-4 mr-1" />Remote</Button>
                          <Button variant="outline" size="sm" className="rounded-full bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100" onClick={() => openFlash(s)} title="Push a design or playlist for N minutes, then auto-revert" data-testid={`screen-flash-${s.id}`}><Zap className="h-4 w-4 mr-1" />Flash</Button>
                          <Button variant="outline" size="sm" className="rounded-full bg-slate-50 border-slate-200 text-slate-900 hover:bg-slate-100" onClick={() => refreshScreen(s.id)} title="Force the TV to reload" data-testid={`screen-refresh-${s.id}`}><RefreshCw className="h-4 w-4 mr-1" />Refresh</Button>
                          <Button variant="outline" size="sm" className="rounded-full bg-slate-50 border-slate-200 text-slate-900 hover:bg-slate-100" onClick={() => openSchedule(s)} data-testid={`screen-schedule-${s.id}`}><Calendar className="h-4 w-4 mr-1" />Schedule</Button>
                          <Button variant="outline" size="sm" className="rounded-full bg-slate-50 border-slate-200 text-slate-900 hover:bg-slate-100" onClick={() => openPowerSchedule(s)} title="Set the screen's open/closed hours" data-testid={`screen-hours-${s.id}`}><Clock4 className="h-4 w-4 mr-1" />Hours</Button>
                          <Button variant="outline" size="sm" className="rounded-full bg-slate-50 border-slate-200 text-slate-900 hover:bg-slate-100" onClick={() => { setRenamingScreen(s); setNewName(s.name || ""); }} data-testid={`screen-rename-${s.id}`}>Rename</Button>
                          <Button variant="outline" size="sm" className="rounded-full bg-slate-50 border-slate-200 text-rose-500 hover:bg-rose-50 hover:text-rose-700" onClick={() => removeScreen(s.id)} data-testid={`screen-delete-${s.id}`}><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Rename dialog */}
      <Dialog open={!!renamingScreen} onOpenChange={(o) => !o && setRenamingScreen(null)}>
        <DialogContent className="rounded-2xl bg-white border-slate-200 text-slate-900">
          <DialogHeader><DialogTitle style={headingFont}>Rename screen</DialogTitle></DialogHeader>
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} className="rounded-xl bg-slate-50 border-slate-200 text-slate-900" data-testid="rename-input" />
          <DialogFooter>
            <Button variant="outline" className="rounded-full bg-slate-50 border-slate-200 text-slate-900 hover:bg-slate-100" onClick={() => setRenamingScreen(null)}>Cancel</Button>
            <Button className="rounded-full bg-sky-500 hover:bg-sky-600 text-white shadow-sm" onClick={renameScreen} data-testid="rename-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule dialog */}
      <Dialog open={!!scheduleScreen} onOpenChange={(o) => !o && setScheduleScreen(null)}>
        <DialogContent className="rounded-2xl max-w-2xl max-h-[85vh] overflow-y-auto bg-white border-slate-200 text-slate-900">
          <DialogHeader>
            <DialogTitle style={headingFont}>Schedule for {scheduleScreen?.name || "screen"}</DialogTitle>
            <DialogDescription className="text-slate-500">
              Add rules to switch designs by time of day. Rules are evaluated in order — the first match wins.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3" data-testid="schedule-rules">
            {scheduleDraft.length === 0 && (
              <div className="text-sm text-slate-500 bg-slate-50 border border-dashed border-slate-200 p-6 text-center rounded-xl" data-testid="schedule-empty">
                No schedule rules. The default design will play all day.
              </div>
            )}
            {scheduleDraft.map((r, idx) => (
              <div key={idx} className="rounded-xl border border-slate-200 p-4 space-y-3 bg-white" data-testid={`rule-${idx}`}>
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px_120px_auto] gap-2 items-end">
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-medium">Design</Label>
                    <Select value={r.design_id || ""} onValueChange={(v) => updateRule(idx, { design_id: v })}>
                      <SelectTrigger className="rounded-xl h-10 mt-1 bg-slate-50 border-slate-200 text-slate-900" data-testid={`rule-design-${idx}`}><SelectValue placeholder="Pick a design…" /></SelectTrigger>
                      <SelectContent>
                        {designs.map((d) => (<SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-medium">From</Label>
                    <Input type="time" value={r.start} onChange={(e) => updateRule(idx, { start: e.target.value })} className="rounded-xl mt-1 bg-slate-50 border-slate-200 text-slate-900" data-testid={`rule-start-${idx}`} />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-medium">Until</Label>
                    <Input type="time" value={r.end} onChange={(e) => updateRule(idx, { end: e.target.value })} className="rounded-xl mt-1 bg-slate-50 border-slate-200 text-slate-900" data-testid={`rule-end-${idx}`} />
                  </div>
                  <Button variant="outline" size="sm" className="rounded-full bg-slate-50 border-slate-200 text-rose-500 h-10 hover:bg-rose-50" onClick={() => removeRule(idx)} data-testid={`rule-delete-${idx}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-medium mb-2 block">Days</Label>
                  <div className="flex flex-wrap gap-2">
                    {DAYS.map((d) => (
                      <label key={d.v} className={`px-3 py-1.5 text-xs font-medium uppercase tracking-[0.15em] cursor-pointer rounded-full border transition-colors ${r.days.includes(d.v) ? "bg-sky-500 text-white border-sky-500" : "bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100"}`} data-testid={`rule-day-${idx}-${d.v}`}>
                        <input type="checkbox" className="hidden" checked={r.days.includes(d.v)} onChange={() => toggleDay(idx, d.v)} />
                        {d.l}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            ))}
            <Button variant="outline" className="rounded-xl w-full border-dashed bg-slate-50 border-white/15 text-slate-900 hover:bg-slate-50" onClick={addRule} data-testid="schedule-add-rule"><Plus className="h-4 w-4 mr-1" />Add rule</Button>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full bg-slate-50 border-slate-200 text-slate-900 hover:bg-slate-100" onClick={() => setScheduleScreen(null)}>Cancel</Button>
            <Button className="rounded-full bg-sky-500 hover:bg-sky-600 text-white shadow-sm" onClick={saveSchedule} data-testid="schedule-save">Save schedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Flash Takeover dialog ─────────────────────────────────────────
          Push a design or playlist temporarily to a screen for N minutes,
          then auto-revert. Perfect for emergency alerts, time-bound promos,
          lunch specials, special events. */}
      <Dialog open={!!flashScreen} onOpenChange={(o) => !o && setFlashScreen(null)}>
        <DialogContent className="rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto bg-white border-slate-200 text-slate-900" data-testid="flash-dialog">
          <DialogHeader>
            <DialogTitle style={headingFont} className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-amber-600 fill-amber-600" />
              Flash takeover · {flashScreen?.name || "screen"}
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              Push a design or playlist to this screen <strong>temporarily</strong>. After the time is up, the screen auto-reverts to whatever it was showing before. Great for last-minute promos and emergency alerts.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            {/* Kind picker */}
            <div className="grid grid-cols-2 gap-2">
              {[
                { v: "design", title: "Single design" },
                { v: "playlist", title: "Playlist" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setFlashDraft({ ...flashDraft, kind: opt.v, ref_id: "" })}
                  className={`text-left p-3 rounded-xl border transition-all ${
                    flashDraft.kind === opt.v
                      ? "border-amber-500 bg-amber-50"
                      : "border-slate-200 bg-slate-50 hover:border-slate-300"
                  }`}
                  data-testid={`flash-kind-${opt.v}`}
                >
                  <div className="text-sm font-semibold text-slate-900">{opt.title}</div>
                </button>
              ))}
            </div>

            {/* Content picker — grid of designs OR playlists */}
            <div>
              <Label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-medium">
                Pick {flashDraft.kind}
              </Label>
              <div
                className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2 max-h-64 overflow-y-auto p-2 bg-slate-50 rounded-xl border border-slate-200"
                data-testid="flash-content-grid"
              >
                {(flashDraft.kind === "design" ? designs : playlists).map((it) => {
                  const selected = flashDraft.ref_id === it.id;
                  const thumb = flashDraft.kind === "design"
                    ? (it.config?.elements || []).find((e) => e.kind === "image")?.src
                    : null;
                  return (
                    <button
                      key={it.id}
                      type="button"
                      onClick={() => setFlashDraft({ ...flashDraft, ref_id: it.id })}
                      className={`text-left rounded-lg overflow-hidden border-2 transition-all ${
                        selected ? "border-amber-500 ring-2 ring-amber-200" : "border-slate-200 hover:border-slate-400"
                      }`}
                      data-testid={`flash-pick-${it.id}`}
                    >
                      <div className="aspect-video bg-white flex items-center justify-center text-slate-300 text-[10px] overflow-hidden">
                        {thumb
                          ? <img src={thumb.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL || ""}${thumb}` : thumb} alt="" className="w-full h-full object-contain" loading="lazy" />
                          : <Monitor className="h-5 w-5" />}
                      </div>
                      <div className="p-2 text-xs font-medium text-slate-900 truncate" title={it.name}>{it.name || "Untitled"}</div>
                    </button>
                  );
                })}
                {(flashDraft.kind === "design" ? designs : playlists).length === 0 && (
                  <div className="col-span-full text-center py-6 text-slate-400 text-sm">
                    No {flashDraft.kind}s yet — create one first.
                  </div>
                )}
              </div>
            </div>

            {/* Duration preset chips + custom */}
            <div>
              <Label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-medium">Duration</Label>
              <div className="flex flex-wrap gap-2 mt-2" data-testid="flash-duration-chips">
                {[5, 15, 30, 60, 120].map((m) => {
                  const selected = parseInt(flashDraft.minutes, 10) === m;
                  return (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setFlashDraft({ ...flashDraft, minutes: m })}
                      className={`px-4 h-9 rounded-full text-sm font-medium transition-all ${
                        selected
                          ? "bg-amber-500 text-white border border-amber-500"
                          : "bg-slate-50 text-slate-700 border border-slate-200 hover:border-slate-400"
                      }`}
                      data-testid={`flash-duration-${m}`}
                    >
                      {m >= 60 ? `${m / 60}h` : `${m}m`}
                    </button>
                  );
                })}
                <div className="flex items-center gap-1">
                  <Input
                    type="number"
                    min="1"
                    max="1440"
                    value={flashDraft.minutes}
                    onChange={(e) => setFlashDraft({ ...flashDraft, minutes: e.target.value })}
                    className="w-20 h-9 rounded-full bg-slate-50 border-slate-200 text-center"
                    data-testid="flash-duration-input"
                  />
                  <span className="text-sm text-slate-500">min</span>
                </div>
              </div>
              <p className="text-[11px] text-slate-400 mt-2">
                Max 24h. After {flashDraft.minutes || "?"} min the screen will silently revert to its scheduled / active content.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full bg-slate-50 border-slate-200 text-slate-900 hover:bg-slate-100" onClick={() => setFlashScreen(null)}>Cancel</Button>
            <Button
              onClick={submitFlash}
              disabled={!flashDraft.ref_id}
              className="rounded-full bg-amber-500 hover:bg-amber-600 text-white shadow-sm"
              data-testid="flash-submit-btn"
            >
              <Zap className="h-4 w-4 mr-1.5 fill-white" />
              Flash now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Power Schedule (operating hours) dialog ───────────────────────
          Per-screen weekly open/closed hours. Outside those hours the TV
          renders a branded "Closed" frame (or stays black). Saves the
          customer electricity and extends TV lifespan. */}
      <Dialog open={!!powerScreen} onOpenChange={(o) => !o && setPowerScreen(null)}>
        <DialogContent className="rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto bg-white border-slate-200 text-slate-900" data-testid="power-schedule-dialog">
          <DialogHeader>
            <DialogTitle style={headingFont} className="flex items-center gap-2">
              <Clock4 className="h-5 w-5 text-sky-600" />
              Operating hours · {powerScreen?.name || "screen"}
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              Auto-blank the screen outside your business hours. Saves on electricity, extends TV lifespan, and keeps your audience seeing fresh content only when you&apos;re open.
            </DialogDescription>
          </DialogHeader>
          {powerDraft && (
            <div className="space-y-5">
              {/* Enabled toggle */}
              <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div>
                  <div className="text-sm font-semibold">Enable operating hours</div>
                  <div className="text-xs text-slate-500 mt-0.5">Turn this off to keep the screen always on, ignoring the weekly schedule.</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={!!powerDraft.enabled}
                    onChange={(e) => setPowerDraft({ ...powerDraft, enabled: e.target.checked })}
                    data-testid="power-enabled-toggle"
                  />
                  <div className="w-11 h-6 bg-slate-300 rounded-full peer peer-checked:bg-sky-500 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition peer-checked:after:translate-x-5" />
                </label>
              </div>

              {/* Timezone */}
              <div className={powerDraft.enabled ? "" : "opacity-40 pointer-events-none"}>
                <Label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-medium">Timezone</Label>
                <Select
                  value={powerDraft.timezone || "UTC"}
                  onValueChange={(v) => setPowerDraft({ ...powerDraft, timezone: v })}
                >
                  <SelectTrigger className="rounded-xl h-10 mt-1 bg-slate-50 border-slate-200 text-slate-900" data-testid="power-tz-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {COMMON_TIMEZONES.map((tz) => (
                      <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Weekly grid */}
              <div className={powerDraft.enabled ? "" : "opacity-40 pointer-events-none"}>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-medium">Weekly hours</Label>
                  <Button type="button" variant="ghost" size="sm" className="text-xs text-sky-600 hover:bg-sky-50" onClick={applyToAllDays} data-testid="power-copy-mon">
                    Copy Mon hours →
                  </Button>
                </div>
                <div className="space-y-2 rounded-2xl border border-slate-200 bg-slate-50 p-3" data-testid="power-weekly-grid">
                  {PWR_DAYS.map(({ k, l }) => {
                    const day = powerDraft.weekly[k];
                    const isOpen = !!day;
                    return (
                      <div key={k} className="grid grid-cols-[60px_70px_1fr_1fr] gap-3 items-center" data-testid={`power-day-${k}`}>
                        <div className="text-sm font-semibold text-slate-700">{l}</div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={isOpen}
                            onChange={() => toggleDayOpen(k)}
                            data-testid={`power-day-toggle-${k}`}
                          />
                          <div className="w-9 h-5 bg-slate-300 rounded-full peer peer-checked:bg-sky-500 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition peer-checked:after:translate-x-4" />
                        </label>
                        {isOpen ? (
                          <>
                            <Input type="time" value={day.start} onChange={(e) => setDayTime(k, "start", e.target.value)} className="rounded-lg h-9 bg-white border-slate-200" data-testid={`power-day-start-${k}`} />
                            <Input type="time" value={day.end} onChange={(e) => setDayTime(k, "end", e.target.value)} className="rounded-lg h-9 bg-white border-slate-200" data-testid={`power-day-end-${k}`} />
                          </>
                        ) : (
                          <div className="col-span-2 text-xs text-slate-400 italic">Closed</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Outside-hours mode */}
              <div className={powerDraft.enabled ? "" : "opacity-40 pointer-events-none"}>
                <Label className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-medium">When closed</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {[
                    { v: "closed_frame", title: "Show closed frame", sub: "Branded \u2018We\u2019re closed\u2019 screen with next-open time" },
                    { v: "blank", title: "Blank screen", sub: "Pure black (saves the most power)" },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => setPowerDraft({ ...powerDraft, outside_hours_mode: opt.v })}
                      className={`text-left p-3 rounded-xl border transition-all ${
                        powerDraft.outside_hours_mode === opt.v
                          ? "border-sky-500 bg-sky-50"
                          : "border-slate-200 bg-slate-50 hover:border-slate-300"
                      }`}
                      data-testid={`power-mode-${opt.v}`}
                    >
                      <div className="text-sm font-semibold text-slate-900">{opt.title}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{opt.sub}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-full bg-slate-50 border-slate-200 text-slate-900 hover:bg-slate-100" onClick={() => setPowerScreen(null)}>Cancel</Button>
            <Button className="rounded-full bg-sky-500 hover:bg-sky-600 text-white shadow-sm" onClick={savePowerSchedule} data-testid="power-save-btn">Save hours</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Remote Control dialog (shared with Admin Fleet) ──────────── */}
      <RemoteControlDialog
        screen={remoteScreen}
        onClose={() => setRemoteScreen(null)}
        adminMode={false}
      />
    </DashboardLayout>
  );
}
